import {
  RecommendationClickEventData,
  RecommendationViewEventData,
  SRFieldMapping,
  SRFormData,
  SyneriseFormEvent,
  SyneriseIdentity
} from 'lib/synerise/types'
import { logAnalyticsEvents } from 'shared/analytics'
import { noop } from 'shared/utils/common'
import { retryPromise } from 'shared/utils/repeatLoop'

////////////////////////////////////////////////
///////////////////Synerise////////////////////
////////////////////////////////////////////////

export const syneriseSendFormData = ({
  formData,
  event,
  fieldMapping
}: {
  formData: SRFormData
  event: SyneriseFormEvent
  fieldMapping?: SRFieldMapping
}) => {
  logAnalyticsEvents({ event, ...formData })
  window.SR?.event?.sendFormData(event, formData, fieldMapping)
}

export const syneriseIdentifyUser = async (user: SyneriseIdentity) => {
  if (!window?.SR?.client) return

  const isUserAlreadyIdentified =
    window.SR.client.getIdentityHash() !== '' &&
    window.SR.client.getIdentityHash() ===
      String(window.SR.client.hashIdentity(user.email))

  if (isUserAlreadyIdentified) return

  // `phoneNumber` is deliberately not sent (KAN-3485): Synerise matches on an
  // internationally prefixed number and the API returns it without the prefix.
  const formData = {
    lastname: user.lastName,
    firstname: user.firstName,
    email: user.email,
    birthdate: user.dateOfBirth || ''
  }

  syneriseSendFormData({
    event: SyneriseFormEvent.IDENTIFY,
    formData,
    fieldMapping: {
      lastname: 'lastname',
      firstname: 'firstname',
      email: 'email',
      birthdate: 'birthdate'
    }
  })
}

export const synerisePageVisit = (
  url: string,
  additionalMetaData: Record<string, string>
) => {
  const eventData: Record<string, string> = {
    title: document.title,
    lang: document.documentElement.lang,
    url,
    ogTags: JSON.stringify({
      'og:site_name': document
        .querySelector('meta[property="og:site_name"]')
        ?.getAttribute('content'),
      'og:title': document
        .querySelector('meta[property="og:title"]')
        ?.getAttribute('content'),
      'og:type': document
        .querySelector('meta[property="og:type"]')
        ?.getAttribute('content'),
      'og:url': document
        .querySelector('meta[property="og:url"]')
        ?.getAttribute('content'),
      ...additionalMetaData
    })
  }

  const trigger = () => {
    void window.SR?.event?.pageVisit(eventData).then(() => {
      void window.SR?.dynamicContent?.get()
    })
  }

  logAnalyticsEvents({ event: 'page.visit', ...eventData })
  if (window.SR?.event) {
    trigger()
  } else {
    retryPromise({
      maxRetries: 10,
      retryDelay: 1000,
      check: () => !!window.SR?.event
    })
      .then(trigger)
      .catch(noop)
  }

  // window.SR.event.trackCustomEvent('page.visit', eventData, 'PageVisit')
}

export const syneriseRecommendationView = (
  eventData: RecommendationViewEventData
) => {
  logAnalyticsEvents({ event: 'product.recommendation.view', ...eventData })

  if (window.SR?.event) {
    window.SR?.event?.recommendationView(
      eventData,
      'Recommended items were displayed'
    )
  }
}

export const syneriseRecommendationClick = (
  eventData: RecommendationClickEventData
) => {
  logAnalyticsEvents({ event: 'product.recommendation.click', ...eventData })

  if (window.SR?.event) {
    window.SR?.event?.recommendationClick(
      eventData,
      'Recommended item was clicked'
    )
  }
}
